import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  circle: {
    transformOrigin: 'center',
    transform: 'rotate(126deg)',
  },
  referenceLabel: {
    fontFamily: 'PoppinsSemiBold',
    color: '#B6C8D3',
    fill: '#B6C8D3',
    fontSize: 12,
    lineHeight: '21px',
  },
  resultLabel: {
    fontFamily: 'PoppinsSemiBold',
    color: '#01619B',
    fill: '#01619B',
    fontSize: 27,
    lineHeight: '37px',
  },
  safariResultLabel: {
    fontFamily: 'PoppinsSemiBold',
    color: '#01619B',
    fill: '#01619B',
    fontSize: 27,
    lineHeight: '37px',
    padding: '0 30px',
  },
  muted: {
    color: '#B6C8D3',
  },
});

export default function GaugeProgress({
  percentage,
  intervalIndex,
  width,
  strokeWidth,
  sortedIntervals,
  scale,
  isDownloadingPdfInSafari,
}) {
  const classes = useStyles();

  const R = width / 2 - strokeWidth * 2;
  const circumference = 2 * Math.PI * R;

  const minValue = sortedIntervals[0].start;
  const maxValue = sortedIntervals[sortedIntervals.length - 1].end;

  const labelY = width / 2 + R * Math.cos(0.2 * Math.PI);
  const minMaxLabelY = width / 2 + R * Math.cos(0.2 * Math.PI) + 10;
  const minLabelX = width / 2 - R * Math.sin(0.2 * Math.PI) + 25;
  const maxLabelX = width / 2 + R * Math.sin(0.2 * Math.PI) - 25;

  const arrowX = width / 2 - 8;
  const arrowY = width / 2 - 66.5;
  const arrowAngle = (percentage - 50) * 2.88;

  const percentageLabel = Math.round(percentage);
  if (isDownloadingPdfInSafari) {
    return (
      <div className={classes.safariResultLabel}>
        {percentageLabel} <span className={classes.muted}>out of</span>{' '}
        {maxValue}
      </div>
    );
  }
  return (
    <div
      style={{
        height: `${width}px`,
        width: `${width}px`,
      }}
    >
      <svg
        height={width}
        width={width}
        viewBox={`0 0 ${width} ${width}`}
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
      >
        <filter id="shadow">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="8"
            floodOpacity="1"
            floodColor={sortedIntervals[intervalIndex].color}
          />
        </filter>
        {sortedIntervals.map((interval, i) => {
          // Draw only first and last intervals that are not the actual interval
          if (i === intervalIndex) return null;

          const initialAngle = 126 + (interval.start / scale) * 288;
          const endAngle = 126 + (interval.end / scale) * 288;
          const offset =
            circumference - 2 * Math.PI * ((endAngle - initialAngle) / 360) * R;

          const strokeLinecap =
            i === 0 || i === sortedIntervals.length - 1 ? 'round' : 'butt';

          return (
            <circle
              key={interval.color}
              strokeWidth={strokeWidth}
              fill="transparent"
              r={R}
              cx={width / 2}
              cy={width / 2}
              stroke={interval.color}
              strokeLinecap={strokeLinecap}
              strokeDasharray={`${circumference} ${circumference}`}
              strokeDashoffset={offset}
              className={classes.circle}
              style={{ transform: `rotate(${initialAngle}deg)` }}
            />
          );
        })}
        {sortedIntervals.map((interval, i) => {
          // Draw actual interval with the highlight arc
          if (i !== intervalIndex) return null;
          const initialAngle = 126 + (interval.start / scale) * 288;
          const endAngle = 126 + (interval.end / scale) * 288;
          const offset =
            circumference - 2 * Math.PI * ((endAngle - initialAngle) / 360) * R;
          return (
            <React.Fragment key={interval.color}>
              <circle
                strokeWidth={strokeWidth * 1.15}
                fill="transparent"
                r={R}
                cx={width / 2}
                cy={width / 2}
                stroke="#FFFFFF"
                strokeLinecap="round"
                strokeDasharray={`${circumference} ${circumference}`}
                strokeDashoffset={offset - 0.5}
                className={classes.circle}
                style={{
                  transform: `rotate(${initialAngle}deg)`,
                  filter: 'url(#shadow)',
                }}
              />
              <circle
                strokeWidth={strokeWidth}
                fill="transparent"
                r={R}
                cx={width / 2}
                cy={width / 2}
                stroke={interval.color}
                strokeLinecap="round"
                strokeDasharray={`${circumference} ${circumference}`}
                strokeDashoffset={offset}
                className={classes.circle}
                style={{ transform: `rotate(${initialAngle}deg)` }}
              />
            </React.Fragment>
          );
        })}
        <circle fill="#E8F1F5" r={17} cx={width / 2} cy={width / 2} />
        <circle
          strokeWidth={1}
          fill="transparent"
          r={19}
          cx={width / 2}
          cy={width / 2}
          stroke="#D6E4EA"
        />
        <g
          transform={`translate(${arrowX} ${arrowY}) rotate(${arrowAngle} 8 66.5)`}
        >
          <path
            d="M8,75 C11.972059,75 15.1920491,71.78001 15.1920491,67.8079509 C15.1920491,67.553137 15.178507,67.298503 15.1514801,67.0451264 L8,0 L8,0 L0.848519851,67.0451264 C0.427223486,70.9947798 3.28752203,74.5381347 7.23717545,74.9594311 C7.49055208,74.9864579 7.74518602,75 8,75 Z"
            fill="#4B4B4B"
          ></path>
          <ellipse
            fill="#FFFFFF"
            cx="8"
            cy="66.5"
            rx="2.44848485"
            ry="2.5"
          ></ellipse>
        </g>
        <text
          x={minLabelX}
          y={minMaxLabelY}
          textAnchor="middle"
          className={classes.referenceLabel}
        >
          {minValue}
        </text>
        <text
          x={maxLabelX}
          y={minMaxLabelY}
          textAnchor="middle"
          className={classes.referenceLabel}
        >
          {maxValue}
        </text>
        <text
          x={width / 2}
          y={labelY}
          textAnchor="middle"
          className={classes.resultLabel}
        >
          {percentageLabel}
        </text>
      </svg>
    </div>
  );
}

GaugeProgress.propTypes = {
  percentage: PropTypes.number.isRequired,
  width: PropTypes.number,
  strokeWidth: PropTypes.number,
  sortedIntervals: PropTypes.array,
  intervalIndex: PropTypes.number,
  scale: PropTypes.number,
  isDownloadingPdfInSafari: PropTypes.bool,
};

GaugeProgress.defaultProps = {
  width: 200,
  strokeWidth: 16,
  scale: 100,
};
